/**
 * Understand Others Tutorial Page (Other mode, desktop app + KizunaAI)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows; the ones
 * not retaken for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Follow Meetings, News and Live Streams in Another Language',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'This guide sets up the Sokuji desktop app to translate what you hear into subtitles in your own language: a meeting you only listen to, a foreign-language news broadcast, a YouTube video or a live stream. Sokuji only listens. Nothing is sent into the meeting, and you need neither a microphone nor a virtual audio cable. It uses KizunaAI (Powered by Soniox; the Qwen engine works the same way). The screenshots are from Sokuji 0.44.0 on Windows, following an English live stream in Google Chrome with subtitles in Chinese.',
  },
  steps: [
    {
      title: 'Set up Sokuji with KizunaAI',
      content:
        'If you have not done so yet, follow the <a href="/docs/kizuna-ai/quick-start">KizunaAI quick start</a> to install Sokuji and sign in.',
    },
    {
      title: 'Choose “Understand what others say”',
      content:
        'In the setup guide, choose <strong>Understand what others say</strong>. It puts Sokuji in <strong>Other</strong> mode with subtitles only. If you have already finished the setup, click <strong>Other</strong> in the mode switch at the bottom left of the main window instead.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Set the two languages',
      content:
        'Set <strong>I read</strong> to the language you want the subtitles in, and <strong>they speak</strong> to the language of the meeting or video. Here: I read Chinese, they speak English.',
      screenshot: '02-languages.png',
      tip: 'Set I read to a specific language, not Auto Detect. Sokuji needs to know which language to translate into, and Start stays disabled until you choose one.',
    },
    {
      title: 'Choose what Sokuji listens to',
      content:
        'In Settings, under <strong>Other’s audio</strong>, pick the app that plays the sound: your meeting app (for example <strong>Microsoft Teams</strong> or Zoom), or your browser (here <strong>Google Chrome</strong>) for news, YouTube and live streams. <strong>System Audio (All Applications)</strong> takes everything your computer plays. The original sound keeps playing as usual.',
      screenshot: '03-audio-source.png',
      tip: 'An app often appears in the list only once it is playing sound. Start the video or join the call first, then click the refresh icon.',
    },
    {
      title: 'Start translating',
      content:
        'Click <strong>Start</strong>. Sokuji shows the translation as subtitles in the main window while the original keeps playing. Nothing is spoken aloud: in this mode <strong>Translation I hear</strong>, in the <strong>Speech</strong> block of Settings, stays off unless you switch it on.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Put the subtitles over the video',
      content:
        'Click <strong>Subtitle</strong> in the title bar to turn Sokuji into a slim subtitle window. Move and resize it, for example over the bottom of the video, then click <strong>Start translating</strong>. <strong>Return to main window</strong> brings the full window back.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Pick the specific app rather than System Audio, so that notification sounds and music from other programs are not translated.',
      'Stop the session when the meeting or video ends: a running session keeps using your balance.',
      'Use the download button in the toolbar to save the transcript when you are done.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Do I need a microphone or VB-CABLE for this?',
        answer: 'No. In Other mode Sokuji only listens to the app you choose. The microphone is only needed when your own speech is translated, and the virtual audio cable only when others should hear your translated voice.',
      },
      {
        question: 'Will the other participants notice anything?',
        answer: 'No. Sokuji listens to the sound on your own computer and sends nothing into the meeting.',
      },
      {
        question: 'Can Sokuji read the translation aloud too?',
        answer: 'Yes. Before you start, switch on Translation I hear in the Speech block of Settings and choose the device it plays on; the language label then says I hear instead of I read. In the desktop app this needs Other’s audio set to one app, not System Audio, so that Sokuji does not translate its own voice again. Reading it aloud costs extra.',
      },
      {
        question: 'Can I also talk back in their language?',
        answer: 'Yes. Switch to Both mode and follow the two-way meeting guide.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '用另一种语言跟上会议、新闻和直播',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      '本指南介绍如何设置 Sokuji 桌面应用，把你听到的内容翻译成你自己语言的字幕：只需旁听的会议、外语新闻广播、YouTube 视频或直播。Sokuji 只负责收听，不会向会议发送任何内容，你既不需要麦克风，也不需要虚拟音频线。本指南使用 KizunaAI（Soniox 驱动；Qwen 引擎的用法相同）。截图来自 Windows 上的 Sokuji 0.44.0，在 Google Chrome 中收看一场英语直播，字幕为中文。',
  },
  steps: [
    {
      title: '用 KizunaAI 设置 Sokuji',
      content:
        '如果还没有完成，请按照 <a href="/docs/kizuna-ai/quick-start">KizunaAI 快速入门</a>安装 Sokuji 并登录。',
    },
    {
      title: '选择“听懂对方”',
      content:
        '在设置向导中选择<strong>听懂对方</strong>。它会让 Sokuji 进入<strong>对方</strong>模式，并且只显示字幕。如果已经完成设置，也可以点击主窗口左下角模式切换中的<strong>对方</strong>。',
      screenshot: '01-scenario.png',
    },
    {
      title: '设置两种语言',
      content:
        '将<strong>我阅读</strong>设为你希望字幕使用的语言，将<strong>对方说</strong>设为会议或视频的语言。此处：我阅读中文，对方说英语。',
      screenshot: '02-languages.png',
      tip: '请将“我阅读”设为具体的语言，而不是“自动检测”。Sokuji 需要知道要翻译成哪种语言；在你选定语言之前，“开始”按钮会一直不可用。',
    },
    {
      title: '选择 Sokuji 收听的内容',
      content:
        '在设置的<strong>对方音频</strong>下，选择播放声音的应用：你的会议应用（例如 <strong>Microsoft Teams</strong> 或 Zoom），或者用于新闻、YouTube 和直播的浏览器（此处为 <strong>Google Chrome</strong>）。<strong>System Audio (All Applications)</strong> 会收录电脑播放的所有声音。原声会照常播放。',
      screenshot: '03-audio-source.png',
      tip: '应用通常要在播放声音后才会出现在列表中。请先播放视频或加入通话，然后点击刷新图标。',
    },
    {
      title: '开始翻译',
      content:
        '点击<strong>开始</strong>。原声照常播放的同时，Sokuji 会在主窗口中以字幕显示翻译。不会朗读任何内容：在此模式下，设置<strong>语音</strong>部分中的<strong>我听到的翻译</strong>保持关闭，除非你把它打开。',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '把字幕放到视频上',
      content:
        '点击标题栏中的<strong>字幕</strong>，Sokuji 会变成一个细长的字幕窗口。移动并调整它的大小，例如放在视频底部，然后点击<strong>开始翻译</strong>。点击<strong>返回主窗口</strong>即可恢复完整窗口。',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '选择具体的应用而不是 System Audio，这样其他程序的通知音和音乐就不会被翻译。',
      '会议或视频结束后请停止会话：正在运行的会话会持续消耗你的余额。',
      '结束后可以用工具栏中的下载按钮保存文字记录。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '这需要麦克风或 VB-CABLE 吗？',
        answer: '不需要。在对方模式下，Sokuji 只收听你选择的应用。只有在翻译你自己说的话时才需要麦克风，只有在需要让别人听到你的翻译语音时才需要虚拟音频线。',
      },
      {
        question: '其他参会者会察觉到吗？',
        answer: '不会。Sokuji 收听的是你自己电脑上的声音，不会向会议发送任何内容。',
      },
      {
        question: 'Sokuji 也能把翻译朗读出来吗？',
        answer: '可以。开始之前，在设置的“语音”部分打开“我听到的翻译”，并选择它播放的设备；语言标签随后会从“我阅读”变为“我听到”。在桌面应用中，这要求“对方音频”选的是单个应用而不是 System Audio，以免 Sokuji 把自己的声音再翻译一遍。朗读会额外计费。',
      },
      {
        question: '我也能用他们的语言回话吗？',
        answer: '可以。切换到两者模式，并按照双向会议指南操作。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '会議・ニュース・ライブ配信を別の言語で理解する',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'このガイドでは、耳にした内容をあなたの言語の字幕に翻訳するよう Sokuji デスクトップアプリを設定します。聞くだけの会議、外国語のニュース放送、YouTube の動画やライブ配信が対象です。Sokuji は聞くだけで、会議には何も送りません。マイクも仮想オーディオケーブルも不要です。KizunaAI（Soniox 提供。Qwen エンジンでも同じ手順です）を使用します。スクリーンショットは Windows 版 Sokuji 0.44.0 で、Google Chrome の英語のライブ配信を中国語の字幕で見ているところです。',
  },
  steps: [
    {
      title: 'KizunaAI で Sokuji をセットアップする',
      content:
        'まだの場合は、<a href="/docs/kizuna-ai/quick-start">KizunaAI クイックスタート</a>に沿って Sokuji をインストールし、サインインしてください。',
    },
    {
      title: '「相手の話を理解する」を選ぶ',
      content:
        'セットアップガイドで<strong>相手の話を理解する</strong>を選びます。Sokuji が字幕のみの<strong>相手</strong>モードになります。すでにセットアップを終えている場合は、代わりにメイン画面左下のモード切り替えで<strong>相手</strong>をクリックします。',
      screenshot: '01-scenario.png',
    },
    {
      title: '2 つの言語を設定する',
      content:
        '<strong>読む言語</strong>を字幕に使いたい言語に、<strong>相手が話す言語</strong>を会議や動画の言語に設定します。ここでは、読む言語は中国語、相手が話す言語は英語です。',
      screenshot: '02-languages.png',
      tip: '「読む言語」は「自動検出」ではなく特定の言語に設定してください。Sokuji は翻訳先の言語を知る必要があり、言語を選ぶまで「開始」は押せません。',
    },
    {
      title: 'Sokuji が聞く音を選ぶ',
      content:
        '設定の<strong>相手の音声</strong>で、音を再生しているアプリを選びます。会議アプリ（例：<strong>Microsoft Teams</strong> や Zoom）か、ニュース・YouTube・ライブ配信ならブラウザー（ここでは <strong>Google Chrome</strong>）です。<strong>System Audio (All Applications)</strong> はパソコンで再生されるすべての音を拾います。元の音はいつもどおり再生されます。',
      screenshot: '03-audio-source.png',
      tip: 'アプリは音を再生して初めて一覧に表示されることがよくあります。先に動画を再生するか通話に参加してから、更新アイコンをクリックしてください。',
    },
    {
      title: '翻訳を開始する',
      content:
        '<strong>開始</strong>をクリックします。元の音が流れたまま、Sokuji はメイン画面に翻訳を字幕で表示します。音声での読み上げはありません。このモードでは、設定の<strong>音声</strong>セクションにある<strong>自分に聞こえる翻訳</strong>は、あなたがオンにしない限りオフのままです。',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '字幕を動画の上に重ねる',
      content:
        'タイトルバーの<strong>字幕</strong>をクリックすると、Sokuji が細長い字幕ウィンドウに切り替わります。動画の下部など好きな位置に移動・サイズ調整してから、<strong>翻訳を開始</strong>をクリックします。<strong>メイン画面に戻る</strong>で元のウィンドウに戻ります。',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      'System Audio ではなく特定のアプリを選ぶと、ほかのプログラムの通知音や音楽が翻訳されません。',
      '会議や動画が終わったらセッションを停止してください。実行中のセッションは残高を消費し続けます。',
      '終わったら、ツールバーのダウンロードボタンで文字起こしを保存できます。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: 'マイクや VB-CABLE は必要ですか？',
        answer: 'いいえ。相手モードでは、Sokuji は選んだアプリの音を聞くだけです。マイクが必要なのは自分の発言を翻訳するときだけ、仮想オーディオケーブルが必要なのは翻訳した自分の声を相手に聞かせるときだけです。',
      },
      {
        question: 'ほかの参加者に気づかれますか？',
        answer: 'いいえ。Sokuji はあなたのパソコン上の音を聞くだけで、会議には何も送りません。',
      },
      {
        question: 'Sokuji に翻訳を読み上げさせることもできますか？',
        answer: 'はい。開始する前に、設定の「音声」セクションで「自分に聞こえる翻訳」をオンにし、再生するデバイスを選びます。言語のラベルは「読む言語」から「聞く言語」に変わります。デスクトップアプリでは、Sokuji が自分の翻訳音声をもう一度翻訳しないよう、「相手の音声」を System Audio ではなく 1 つのアプリに設定する必要があります。読み上げには追加料金がかかります。',
      },
      {
        question: '相手の言語で返事をすることもできますか？',
        answer: 'はい。両方モードに切り替えて、双方向の会議ガイドに沿って設定してください。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '다른 언어로 된 회의, 뉴스, 라이브 방송 따라가기',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      '이 가이드는 귀에 들리는 내용을 내 언어의 자막으로 번역하도록 Sokuji 데스크톱 앱을 설정합니다. 듣기만 하는 회의, 외국어 뉴스 방송, YouTube 동영상이나 라이브 방송이 대상입니다. Sokuji는 듣기만 하며 회의에는 아무것도 보내지 않으므로, 마이크도 가상 오디오 케이블도 필요 없습니다. KizunaAI(Soniox 제공이며, Qwen 엔진도 같은 방식으로 작동합니다)를 사용합니다. 스크린샷은 Windows용 Sokuji 0.44.0에서 Google Chrome의 영어 라이브 방송을 중국어 자막으로 보는 모습입니다.',
  },
  steps: [
    {
      title: 'KizunaAI로 Sokuji 설정',
      content:
        '아직 하지 않았다면 <a href="/docs/kizuna-ai/quick-start">KizunaAI 빠른 시작</a>을 따라 Sokuji를 설치하고 로그인하세요.',
    },
    {
      title: '“상대방의 말 이해하기” 선택',
      content:
        '설정 가이드에서 <strong>상대방의 말 이해하기</strong>를 선택하세요. Sokuji가 자막만 표시하는 <strong>상대방</strong> 모드로 설정됩니다. 이미 설정을 마쳤다면 대신 메인 창 왼쪽 아래의 모드 전환에서 <strong>상대방</strong>을 클릭하세요.',
      screenshot: '01-scenario.png',
    },
    {
      title: '두 언어 설정',
      content:
        '<strong>내가 읽는 언어</strong>를 자막으로 보고 싶은 언어로, <strong>상대방이 말하는 언어</strong>를 회의나 동영상의 언어로 설정하세요. 여기서는 내가 읽는 언어는 중국어, 상대방이 말하는 언어는 영어입니다.',
      screenshot: '02-languages.png',
      tip: '“내가 읽는 언어”는 “자동 감지”가 아닌 특정 언어로 설정하세요. Sokuji가 어떤 언어로 번역할지 알아야 하므로, 언어를 고를 때까지 “시작”이 비활성화됩니다.',
    },
    {
      title: 'Sokuji가 들을 소리 선택',
      content:
        '설정의 <strong>상대방 오디오</strong>에서 소리를 재생하는 앱을 고르세요. 회의 앱(예: <strong>Microsoft Teams</strong> 또는 Zoom)이나, 뉴스·YouTube·라이브 방송이라면 브라우저(여기서는 <strong>Google Chrome</strong>)입니다. <strong>System Audio (All Applications)</strong>는 컴퓨터에서 재생되는 모든 소리를 받습니다. 원래 소리는 평소처럼 계속 재생됩니다.',
      screenshot: '03-audio-source.png',
      tip: '앱은 소리를 재생해야 목록에 나타나는 경우가 많습니다. 먼저 동영상을 재생하거나 통화에 참여한 다음 새로고침 아이콘을 클릭하세요.',
    },
    {
      title: '번역 시작',
      content:
        '<strong>시작</strong>을 클릭하세요. 원래 소리가 계속 재생되는 동안 Sokuji가 메인 창에 번역을 자막으로 보여 줍니다. 소리 내어 읽어 주지는 않습니다. 이 모드에서는 설정의 <strong>음성</strong> 섹션에 있는 <strong>내가 듣는 번역</strong>이 꺼져 있으며, 직접 켜지 않는 한 꺼진 상태로 유지됩니다.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: '자막을 동영상 위에 띄우기',
      content:
        '제목 표시줄의 <strong>자막</strong>을 클릭하면 Sokuji가 얇은 자막 창으로 바뀝니다. 예를 들어 동영상 아래쪽에 오도록 창을 옮기고 크기를 조절한 다음 <strong>번역 시작</strong>을 클릭하세요. <strong>메인 창으로 돌아가기</strong>를 누르면 전체 창으로 돌아옵니다.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      'System Audio 대신 특정 앱을 고르면 다른 프로그램의 알림음이나 음악이 번역되지 않습니다.',
      '회의나 동영상이 끝나면 세션을 중지하세요. 실행 중인 세션은 계속 잔액을 사용합니다.',
      '끝나면 도구 모음의 다운로드 버튼으로 대화 기록을 저장할 수 있습니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '마이크나 VB-CABLE이 필요한가요?',
        answer: '아니요. 상대방 모드에서 Sokuji는 선택한 앱의 소리만 듣습니다. 마이크는 내 말을 번역할 때만, 가상 오디오 케이블은 번역된 내 목소리를 다른 사람에게 들려줄 때만 필요합니다.',
      },
      {
        question: '다른 참가자들이 눈치챌까요?',
        answer: '아니요. Sokuji는 내 컴퓨터의 소리를 들을 뿐, 회의에는 아무것도 보내지 않습니다.',
      },
      {
        question: 'Sokuji가 번역을 소리 내어 읽어 줄 수도 있나요?',
        answer: '네. 시작하기 전에 설정의 “음성” 섹션에서 “내가 듣는 번역”을 켜고 재생할 장치를 고르세요. 그러면 언어 표시가 “내가 읽는 언어” 대신 “내가 듣는 언어”로 바뀝니다. 데스크톱 앱에서는 Sokuji가 자기 음성을 다시 번역하지 않도록 “상대방 오디오”를 System Audio가 아닌 앱 하나로 설정해야 합니다. 소리 내어 읽어 주면 추가 비용이 듭니다.',
      },
      {
        question: '상대방의 언어로 대답할 수도 있나요?',
        answer: '네. 모두 모드로 전환하고 양방향 회의 가이드를 따르세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Sigue reuniones, noticias y directos en otro idioma',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'Esta guía configura la aplicación de escritorio de Sokuji para traducir lo que oyes a subtítulos en tu propio idioma: una reunión en la que solo escuchas, un informativo en otro idioma, un vídeo de YouTube o un directo. Sokuji solo escucha. No se envía nada a la reunión, y no necesitas micrófono ni cable de audio virtual. Usa KizunaAI (Con tecnología de Soniox; el motor de Qwen funciona igual). Las capturas son de Sokuji 0.44.0 en Windows, siguiendo un directo en inglés en Google Chrome con subtítulos en chino.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Si aún no lo has hecho, sigue el <a href="/docs/kizuna-ai/quick-start">inicio rápido de KizunaAI</a> para instalar Sokuji e iniciar sesión.',
    },
    {
      title: 'Elige «Entender lo que dicen los demás»',
      content:
        'En la guía de configuración, elige <strong>Entender lo que dicen los demás</strong>. Pone Sokuji en modo <strong>Otro</strong>, solo con subtítulos. Si ya terminaste la configuración, haz clic en <strong>Otro</strong> en el selector de modo, abajo a la izquierda de la ventana principal.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Configura los dos idiomas',
      content:
        'Pon en <strong>Yo leo</strong> el idioma en el que quieres los subtítulos, y en <strong>ellos hablan</strong> el idioma de la reunión o del vídeo. Aquí: Yo leo Chino, ellos hablan Inglés.',
      screenshot: '02-languages.png',
      tip: 'Pon en Yo leo un idioma concreto, no Detección Automática. Sokuji necesita saber a qué idioma traducir, e Iniciar sigue desactivado hasta que elijas uno.',
    },
    {
      title: 'Elige qué escucha Sokuji',
      content:
        'En Configuración, en <strong>Audio del otro</strong>, elige la aplicación que reproduce el sonido: tu aplicación de reuniones (por ejemplo <strong>Microsoft Teams</strong> o Zoom), o tu navegador (aquí <strong>Google Chrome</strong>) para noticias, YouTube y directos. <strong>System Audio (All Applications)</strong> recoge todo lo que suena en tu ordenador. El sonido original se sigue reproduciendo como siempre.',
      screenshot: '03-audio-source.png',
      tip: 'A menudo una aplicación solo aparece en la lista cuando está reproduciendo sonido. Inicia primero el vídeo o únete a la llamada y luego haz clic en el icono de actualizar.',
    },
    {
      title: 'Empieza a traducir',
      content:
        'Haz clic en <strong>Iniciar</strong>. Sokuji muestra la traducción como subtítulos en la ventana principal mientras el original se sigue reproduciendo. No se pronuncia nada en voz alta: en este modo, <strong>Traducción que yo oigo</strong>, en la sección <strong>Voz</strong> de Configuración, permanece desactivada salvo que la actives.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Coloca los subtítulos sobre el vídeo',
      content:
        'Haz clic en <strong>Subtítulos</strong> en la barra de título para convertir Sokuji en una ventana de subtítulos estrecha. Muévela y cambia su tamaño, por ejemplo sobre la parte inferior del vídeo, y haz clic en <strong>Empezar a traducir</strong>. <strong>Volver a la ventana principal</strong> recupera la ventana completa.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Elige la aplicación concreta en lugar de System Audio, para que no se traduzcan los sonidos de notificación ni la música de otros programas.',
      'Detén la sesión cuando termine la reunión o el vídeo: una sesión en marcha sigue consumiendo tu saldo.',
      'Cuando acabes, usa el botón de descarga de la barra de herramientas para guardar la transcripción.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Necesito un micrófono o VB-CABLE para esto?',
        answer: 'No. En el modo Otro, Sokuji solo escucha la aplicación que elijas. El micrófono solo hace falta cuando se traduce lo que tú dices, y el cable de audio virtual solo cuando los demás deben oír tu voz traducida.',
      },
      {
        question: '¿Notarán algo los demás participantes?',
        answer: 'No. Sokuji escucha el sonido de tu propio ordenador y no envía nada a la reunión.',
      },
      {
        question: '¿Puede Sokuji leer también la traducción en voz alta?',
        answer: 'Sí. Antes de empezar, activa Traducción que yo oigo en la sección Voz de Configuración y elige el dispositivo en el que se reproduce; la etiqueta de idioma dice entonces Yo escucho en lugar de Yo leo. En la aplicación de escritorio, esto requiere que Audio del otro esté configurado con una aplicación, no con System Audio, para que Sokuji no vuelva a traducir su propia voz. Leerla en voz alta cuesta más.',
      },
      {
        question: '¿Puedo también responder en su idioma?',
        answer: 'Sí. Cambia al modo Ambos y sigue la guía de reuniones bidireccionales.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Suivre des réunions, des journaux télévisés et des directs dans une autre langue',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'Ce guide configure l’application de bureau Sokuji pour traduire ce que vous entendez en sous-titres dans votre propre langue : une réunion que vous suivez sans parler, un journal télévisé en langue étrangère, une vidéo YouTube ou un direct. Sokuji ne fait qu’écouter. Rien n’est envoyé dans la réunion, et vous n’avez besoin ni de microphone ni de câble audio virtuel. Il utilise KizunaAI (Propulsé par Soniox ; le moteur Qwen fonctionne de la même façon). Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows, pendant un direct en anglais dans Google Chrome avec des sous-titres en chinois.',
  },
  steps: [
    {
      title: 'Configurez Sokuji avec KizunaAI',
      content:
        'Si ce n’est pas encore fait, suivez le <a href="/docs/kizuna-ai/quick-start">démarrage rapide KizunaAI</a> pour installer Sokuji et vous connecter.',
    },
    {
      title: 'Choisissez « Comprendre ce que disent les autres »',
      content:
        'Dans le guide de configuration, choisissez <strong>Comprendre ce que disent les autres</strong>. Sokuji passe alors en mode <strong>Autre</strong>, avec des sous-titres uniquement. Si vous avez déjà terminé la configuration, cliquez plutôt sur <strong>Autre</strong> dans le sélecteur de mode, en bas à gauche de la fenêtre principale.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Réglez les deux langues',
      content:
        'Réglez <strong>je lis</strong> sur la langue dans laquelle vous voulez les sous-titres, et <strong>ils parlent</strong> sur la langue de la réunion ou de la vidéo. Ici : je lis chinois, ils parlent anglais.',
      screenshot: '02-languages.png',
      tip: 'Réglez je lis sur une langue précise, pas sur Détection Automatique. Sokuji doit savoir vers quelle langue traduire, et Démarrer reste désactivé tant que vous n’en avez pas choisi une.',
    },
    {
      title: 'Choisissez ce que Sokuji écoute',
      content:
        'Dans les Paramètres, sous <strong>Audio de l\'autre</strong>, sélectionnez l’application qui émet le son : votre application de réunion (par exemple <strong>Microsoft Teams</strong> ou Zoom), ou votre navigateur (ici <strong>Google Chrome</strong>) pour les journaux, YouTube et les directs. <strong>System Audio (All Applications)</strong> capte tout ce que votre ordinateur diffuse. Le son d’origine continue d’être diffusé normalement.',
      screenshot: '03-audio-source.png',
      tip: 'Une application n’apparaît souvent dans la liste qu’une fois qu’elle émet du son. Lancez d’abord la vidéo ou rejoignez l’appel, puis cliquez sur l’icône d’actualisation.',
    },
    {
      title: 'Lancez la traduction',
      content:
        'Cliquez sur <strong>Démarrer</strong>. Sokuji affiche la traduction en sous-titres dans la fenêtre principale pendant que l’original continue d’être diffusé. Rien n’est prononcé à voix haute : dans ce mode, <strong>Traduction que j\'entends</strong>, dans la section <strong>Voix</strong> des Paramètres, reste désactivée à moins que vous ne l’activiez.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Placez les sous-titres sur la vidéo',
      content:
        'Cliquez sur <strong>Sous-titres</strong> dans la barre de titre pour transformer Sokuji en une fine fenêtre de sous-titres. Déplacez-la et redimensionnez-la, par exemple au bas de la vidéo, puis cliquez sur <strong>Démarrer la traduction</strong>. <strong>Retour à la fenêtre principale</strong> rétablit la fenêtre complète.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Choisissez l’application précise plutôt que System Audio, pour que les sons de notification et la musique des autres programmes ne soient pas traduits.',
      'Arrêtez la session à la fin de la réunion ou de la vidéo : une session en cours continue de consommer votre solde.',
      'Une fois terminé, utilisez le bouton de téléchargement de la barre d’outils pour enregistrer la transcription.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Ai-je besoin d’un microphone ou de VB-CABLE pour cela ?',
        answer: 'Non. En mode Autre, Sokuji écoute uniquement l’application que vous choisissez. Le microphone n’est nécessaire que pour traduire vos propres paroles, et le câble audio virtuel seulement quand les autres doivent entendre votre voix traduite.',
      },
      {
        question: 'Les autres participants remarqueront-ils quelque chose ?',
        answer: 'Non. Sokuji écoute le son de votre propre ordinateur et n’envoie rien dans la réunion.',
      },
      {
        question: 'Sokuji peut-il aussi lire la traduction à voix haute ?',
        answer: 'Oui. Avant de démarrer, activez « Traduction que j\'entends » dans la section Voix des Paramètres et choisissez l’appareil sur lequel elle est lue ; l’indication de langue affiche alors « j\'entends » au lieu de « je lis ». Dans l’application de bureau, il faut pour cela que Audio de l\'autre soit réglé sur une seule application, et non sur System Audio, pour que Sokuji ne retraduise pas sa propre voix. La lecture à voix haute coûte plus cher.',
      },
      {
        question: 'Puis-je aussi répondre dans leur langue ?',
        answer: 'Oui. Passez en mode Les deux et suivez le guide des réunions bidirectionnelles.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'تابِع الاجتماعات والأخبار والبث المباشر بلغة أخرى',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'يُعدّ هذا الدليل تطبيق Sokuji لسطح المكتب ليترجم ما تسمعه إلى ترجمة نصية بلغتك: اجتماع تكتفي بالاستماع إليه، أو نشرة أخبار بلغة أجنبية، أو فيديو على YouTube، أو بث مباشر. يكتفي Sokuji بالاستماع، فلا يُرسَل أي شيء إلى الاجتماع، ولا تحتاج إلى ميكروفون ولا إلى كابل صوت افتراضي. يستخدم الدليل KizunaAI (مدعوم من Soniox؛ ويعمل محرك Qwen بالطريقة نفسها). لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows أثناء متابعة بث مباشر باللغة الإنجليزية في Google Chrome مع ترجمة نصية بالصينية.',
  },
  steps: [
    {
      title: 'اضبط Sokuji مع KizunaAI',
      content:
        'إذا لم تفعل ذلك بعد، فاتبع <a href="/docs/kizuna-ai/quick-start">البدء السريع مع KizunaAI</a> لتثبيت Sokuji وتسجيل الدخول.',
    },
    {
      title: 'اختر «افهم ما يقوله الآخرون»',
      content:
        'في دليل الإعداد، اختر <strong>افهم ما يقوله الآخرون</strong>. يضع ذلك Sokuji في وضع <strong>الآخر</strong> مع ترجمة نصية فقط. إذا كنت قد أنهيت الإعداد بالفعل، فانقر بدلًا من ذلك على <strong>الآخر</strong> في مبدّل الأوضاع أسفل يسار النافذة الرئيسية.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'اضبط اللغتين',
      content:
        'اضبط <strong>أقرأ</strong> على اللغة التي تريد الترجمة النصية بها، و<strong>يتحدثون</strong> على لغة الاجتماع أو الفيديو. هنا: أقرأ الصينية، يتحدثون الإنجليزية.',
      screenshot: '02-languages.png',
      tip: 'اضبط «أقرأ» على لغة محددة، لا على «اكتشاف تلقائي». يحتاج Sokuji إلى معرفة اللغة التي يترجم إليها، ويبقى زر «ابدأ» معطّلًا حتى تختار لغة.',
    },
    {
      title: 'اختر ما يستمع إليه Sokuji',
      content:
        'في الإعدادات، تحت <strong>صوت الآخر</strong>، اختر التطبيق الذي يشغّل الصوت: تطبيق الاجتماع (مثل <strong>Microsoft Teams</strong> أو Zoom)، أو المتصفح (هنا <strong>Google Chrome</strong>) للأخبار وYouTube والبث المباشر. أما <strong>System Audio (All Applications)</strong> فيلتقط كل ما يشغّله جهازك. ويستمر الصوت الأصلي في التشغيل كالمعتاد.',
      screenshot: '03-audio-source.png',
      tip: 'غالبًا لا يظهر التطبيق في القائمة إلا بعد أن يشغّل صوتًا. شغّل الفيديو أو انضم إلى المكالمة أولًا، ثم انقر على أيقونة التحديث.',
    },
    {
      title: 'ابدأ الترجمة',
      content:
        'انقر على <strong>ابدأ</strong>. يعرض Sokuji الترجمة كترجمة نصية في النافذة الرئيسية بينما يستمر تشغيل الصوت الأصلي. لا يُنطق أي شيء بصوت مسموع: ففي هذا الوضع يبقى خيار <strong>الترجمة التي أسمعها</strong>، في قسم <strong>الكلام</strong> في الإعدادات، متوقفًا ما لم تفعّله.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'ضع الترجمة النصية فوق الفيديو',
      content:
        'انقر على <strong>ترجمة</strong> في شريط العنوان ليتحوّل Sokuji إلى نافذة ترجمة نصية نحيفة. حرّكها وغيّر حجمها، فوق أسفل الفيديو مثلًا، ثم انقر على <strong>بدء الترجمة</strong>. ويعيدك <strong>العودة إلى النافذة الرئيسية</strong> إلى النافذة الكاملة.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'اختر التطبيق المحدد بدلًا من System Audio، حتى لا تُترجَم أصوات الإشعارات والموسيقى الصادرة عن البرامج الأخرى.',
      'أوقف الجلسة عند انتهاء الاجتماع أو الفيديو: فالجلسة الجارية تستمر في استهلاك رصيدك.',
      'عند الانتهاء، استخدم زر التنزيل في شريط الأدوات لحفظ النص المكتوب.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل أحتاج إلى ميكروفون أو VB-CABLE لهذا؟',
        answer: 'لا. في وضع الآخر يستمع Sokuji فقط إلى التطبيق الذي تختاره. لا تحتاج إلى الميكروفون إلا عند ترجمة كلامك أنت، ولا إلى كابل الصوت الافتراضي إلا عندما يجب أن يسمع الآخرون صوتك المترجَم.',
      },
      {
        question: 'هل سيلاحظ المشاركون الآخرون أي شيء؟',
        answer: 'لا. يستمع Sokuji إلى الصوت على جهازك أنت ولا يرسل أي شيء إلى الاجتماع.',
      },
      {
        question: 'هل يمكن لـ Sokuji قراءة الترجمة بصوت مسموع أيضًا؟',
        answer: 'نعم. قبل أن تبدأ، فعّل «الترجمة التي أسمعها» في قسم «الكلام» في الإعدادات واختر الجهاز الذي تُشغَّل عليه؛ عندها تقول تسمية اللغة «أسمع» بدلًا من «أقرأ». في تطبيق سطح المكتب، يتطلب ذلك ضبط «صوت الآخر» على تطبيق واحد، لا على System Audio، حتى لا يترجم Sokuji صوته هو مرة أخرى. وقراءة الترجمة بصوت مسموع تكلّف أكثر.',
      },
      {
        question: 'هل يمكنني أيضًا الرد بلغتهم؟',
        answer: 'نعم. انتقل إلى وضع الكل واتبع دليل الاجتماعات ثنائية الاتجاه.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Acompanhe reuniões, noticiários e transmissões ao vivo em outro idioma',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Este guia configura o aplicativo de desktop do Sokuji para traduzir o que você ouve em legendas no seu idioma: uma reunião em que você só escuta, um noticiário em língua estrangeira, um vídeo do YouTube ou uma transmissão ao vivo. O Sokuji apenas escuta. Nada é enviado para a reunião, e você não precisa de microfone nem de cabo de áudio virtual. Ele usa a KizunaAI (Com tecnologia Soniox; o mesmo vale para o mecanismo Qwen). As capturas de tela são do Sokuji 0.44.0 no Windows, acompanhando uma transmissão ao vivo em inglês no Google Chrome com legendas em chinês.',
  },
  steps: [
    {
      title: 'Configure o Sokuji com a KizunaAI',
      content:
        'Se ainda não fez isso, siga o <a href="/docs/kizuna-ai/quick-start">início rápido com KizunaAI</a> para instalar o Sokuji e entrar na sua conta.',
    },
    {
      title: 'Escolha “Entender o que os outros dizem”',
      content:
        'No guia de configuração, escolha <strong>Entender o que os outros dizem</strong>. Isso coloca o Sokuji no modo <strong>Outro</strong>, só com legendas. Se você já concluiu a configuração, clique em <strong>Outro</strong> no seletor de modo, no canto inferior esquerdo da janela principal.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Defina os dois idiomas',
      content:
        'Defina <strong>eu leio</strong> como o idioma em que você quer as legendas e <strong>eles falam</strong> como o idioma da reunião ou do vídeo. Aqui: eu leio Chinês, eles falam Inglês.',
      screenshot: '02-languages.png',
      tip: 'Defina eu leio como um idioma específico, não como Detecção Automática. O Sokuji precisa saber para qual idioma traduzir, e Iniciar continua desativado até você escolher um.',
    },
    {
      title: 'Escolha o que o Sokuji escuta',
      content:
        'Em Configurações, em <strong>Áudio do outro</strong>, escolha o aplicativo que reproduz o som: seu aplicativo de reunião (por exemplo, <strong>Microsoft Teams</strong> ou Zoom) ou seu navegador (aqui o <strong>Google Chrome</strong>) para noticiários, YouTube e transmissões ao vivo. <strong>System Audio (All Applications)</strong> capta tudo o que o computador reproduz. O som original continua tocando normalmente.',
      screenshot: '03-audio-source.png',
      tip: 'Muitas vezes um aplicativo só aparece na lista quando está reproduzindo som. Inicie o vídeo ou entre na chamada primeiro e depois clique no ícone de atualizar.',
    },
    {
      title: 'Comece a traduzir',
      content:
        'Clique em <strong>Iniciar</strong>. O Sokuji mostra a tradução como legendas na janela principal enquanto o original continua tocando. Nada é falado em voz alta: neste modo, <strong>Tradução que eu ouço</strong>, na seção <strong>Voz</strong> das Configurações, fica desativada, a menos que você a ative.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Coloque as legendas sobre o vídeo',
      content:
        'Clique em <strong>Legendas</strong> na barra de título para transformar o Sokuji em uma janela de legendas compacta. Mova e redimensione a janela, por exemplo sobre a parte de baixo do vídeo, e clique em <strong>Iniciar tradução</strong>. <strong>Voltar à janela principal</strong> traz a janela completa de volta.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Escolha o aplicativo específico em vez de System Audio, para que sons de notificação e músicas de outros programas não sejam traduzidos.',
      'Encerre a sessão quando a reunião ou o vídeo terminar: uma sessão em andamento continua consumindo seu saldo.',
      'Use o botão de download na barra de ferramentas para salvar a transcrição quando terminar.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Preciso de microfone ou do VB-CABLE para isso?',
        answer: 'Não. No modo Outro, o Sokuji só escuta o aplicativo que você escolher. O microfone só é necessário quando a sua própria fala é traduzida, e o cabo de áudio virtual só quando os outros precisam ouvir a sua voz traduzida.',
      },
      {
        question: 'Os outros participantes vão perceber alguma coisa?',
        answer: 'Não. O Sokuji escuta o som no seu próprio computador e não envia nada para a reunião.',
      },
      {
        question: 'O Sokuji também pode ler a tradução em voz alta?',
        answer: 'Sim. Antes de iniciar, ative Tradução que eu ouço na seção Voz das Configurações e escolha o dispositivo em que ela toca; o rótulo de idioma passa então a dizer “eu ouço” em vez de “eu leio”. No aplicativo de desktop, isso exige que Áudio do outro esteja definido como um aplicativo, e não como System Audio, para que o Sokuji não traduza de novo a própria voz. A leitura em voz alta custa mais.',
      },
      {
        question: 'Também posso responder no idioma deles?',
        answer: 'Sim. Mude para o modo Ambos e siga o guia de reuniões bidirecionais.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Следите за встречами, новостями и прямыми трансляциями на другом языке',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Это руководство настраивает настольное приложение Sokuji так, чтобы то, что вы слышите, переводилось в субтитры на вашем языке: встреча, которую вы только слушаете, выпуск новостей на иностранном языке, видео на YouTube или прямая трансляция. Sokuji только слушает. Во встречу ничего не отправляется, и вам не нужны ни микрофон, ни виртуальный аудиокабель. Используется KizunaAI (на базе Soniox; движок Qwen работает так же). Снимки экрана сделаны в Sokuji 0.44.0 на Windows во время просмотра англоязычной трансляции в Google Chrome с субтитрами на китайском.',
  },
  steps: [
    {
      title: 'Настройте Sokuji с KizunaAI',
      content:
        'Если вы ещё этого не сделали, следуйте руководству <a href="/docs/kizuna-ai/quick-start">«Быстрый старт с KizunaAI»</a>, чтобы установить Sokuji и войти в учётную запись.',
    },
    {
      title: 'Выберите «Понимать, что говорят другие»',
      content:
        'В мастере настройки выберите <strong>Понимать, что говорят другие</strong>. Sokuji переходит в режим <strong>Собеседник</strong> только с субтитрами. Если вы уже завершили настройку, вместо этого нажмите <strong>Собеседник</strong> в переключателе режимов в левом нижнем углу главного окна.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте два языка',
      content:
        'Укажите в поле <strong>я читаю</strong> язык, на котором нужны субтитры, а в поле <strong>собеседник говорит на</strong> — язык встречи или видео. Здесь: я читаю на китайском, собеседник говорит на английском.',
      screenshot: '02-languages.png',
      tip: 'Выберите в поле «я читаю» конкретный язык, а не «Автоопределение». Sokuji должен знать, на какой язык переводить, и кнопка «Старт» остаётся недоступной, пока вы его не выберете.',
    },
    {
      title: 'Выберите, что слушает Sokuji',
      content:
        'В настройках, в разделе <strong>Аудио собеседника</strong>, выберите приложение, которое воспроизводит звук: приложение для встреч (например, <strong>Microsoft Teams</strong> или Zoom) или браузер (здесь <strong>Google Chrome</strong>) для новостей, YouTube и прямых трансляций. <strong>System Audio (All Applications)</strong> захватывает всё, что воспроизводит компьютер. Исходный звук по-прежнему воспроизводится как обычно.',
      screenshot: '03-audio-source.png',
      tip: 'Приложение часто появляется в списке только тогда, когда воспроизводит звук. Сначала запустите видео или подключитесь к звонку, затем нажмите значок обновления.',
    },
    {
      title: 'Начните перевод',
      content:
        'Нажмите <strong>Старт</strong>. Sokuji показывает перевод в виде субтитров в главном окне, а оригинал продолжает воспроизводиться. Вслух ничего не озвучивается: в этом режиме <strong>Перевод, который слышу я</strong> в блоке <strong>Речь</strong> в настройках остаётся выключенным, пока вы сами его не включите.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Разместите субтитры поверх видео',
      content:
        'Нажмите <strong>Субтитры</strong> в строке заголовка, чтобы превратить Sokuji в узкое окно субтитров. Переместите его и измените размер, например поверх нижней части видео, затем нажмите <strong>Начать перевод</strong>. Кнопка <strong>Вернуться в главное окно</strong> возвращает полное окно.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Выбирайте конкретное приложение, а не System Audio, чтобы звуки уведомлений и музыка из других программ не переводились.',
      'Останавливайте сеанс, когда встреча или видео заканчивается: запущенный сеанс продолжает расходовать ваш баланс.',
      'Когда закончите, сохраните расшифровку кнопкой загрузки на панели инструментов.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Нужны ли для этого микрофон или VB-CABLE?',
        answer: 'Нет. В режиме «Собеседник» Sokuji только слушает выбранное вами приложение. Микрофон нужен только тогда, когда переводится ваша собственная речь, а виртуальный аудиокабель — только когда другие должны слышать ваш переведённый голос.',
      },
      {
        question: 'Заметят ли что-нибудь другие участники?',
        answer: 'Нет. Sokuji слушает звук на вашем собственном компьютере и ничего не отправляет во встречу.',
      },
      {
        question: 'Может ли Sokuji также зачитывать перевод вслух?',
        answer: 'Да. Перед началом включите «Перевод, который слышу я» в блоке «Речь» в настройках и выберите устройство, на котором он воспроизводится; тогда в строке языков вместо «я читаю» будет написано «я слышу». В настольном приложении для этого в «Аудио собеседника» должно быть выбрано одно приложение, а не System Audio, чтобы Sokuji не переводил повторно собственный голос. Зачитывание вслух стоит дополнительно.',
      },
      {
        question: 'Могу ли я тоже отвечать на их языке?',
        answer: 'Да. Переключитесь в режим «Оба» и следуйте руководству по двусторонним встречам.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Meetings, Nachrichten und Livestreams in einer anderen Sprache folgen',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Diese Anleitung richtet die Sokuji-Desktop-App so ein, dass sie das, was Sie hören, als Untertitel in Ihre Sprache übersetzt: ein Meeting, bei dem Sie nur zuhören, eine fremdsprachige Nachrichtensendung, ein YouTube-Video oder einen Livestream. Sokuji hört nur zu. Es wird nichts ins Meeting gesendet, und Sie brauchen weder ein Mikrofon noch ein virtuelles Audiokabel. Verwendet wird KizunaAI (Unterstützt von Soniox; die Qwen-Engine funktioniert genauso). Die Screenshots stammen aus Sokuji 0.44.0 unter Windows und zeigen einen englischen Livestream in Google Chrome mit Untertiteln auf Chinesisch.',
  },
  steps: [
    {
      title: 'Sokuji mit KizunaAI einrichten',
      content:
        'Falls noch nicht geschehen, folgen Sie dem <a href="/docs/kizuna-ai/quick-start">KizunaAI-Schnellstart</a>, um Sokuji zu installieren und sich anzumelden.',
    },
    {
      title: '„Verstehen, was andere sagen“ wählen',
      content:
        'Wählen Sie in der Einrichtung <strong>Verstehen, was andere sagen</strong>. Damit wechselt Sokuji in den Modus <strong>Gegenüber</strong>, nur mit Untertiteln. Wenn Sie die Einrichtung bereits abgeschlossen haben, klicken Sie stattdessen im Modusschalter unten links im Hauptfenster auf <strong>Gegenüber</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Die beiden Sprachen einstellen',
      content:
        'Stellen Sie bei <strong>Ich lese</strong> die Sprache ein, in der Sie die Untertitel möchten, und bei <strong>sie sprechen</strong> die Sprache des Meetings oder Videos. Hier: Ich lese Chinesisch, sie sprechen Englisch.',
      screenshot: '02-languages.png',
      tip: 'Stellen Sie „Ich lese“ auf eine bestimmte Sprache, nicht auf „Automatische Erkennung“. Sokuji muss wissen, in welche Sprache es übersetzen soll, und „Starten“ bleibt deaktiviert, bis Sie eine auswählen.',
    },
    {
      title: 'Wählen, was Sokuji abhört',
      content:
        'Wählen Sie in den Einstellungen unter <strong>Audio des Gegenübers</strong> die App, die den Ton wiedergibt: Ihre Meeting-App (zum Beispiel <strong>Microsoft Teams</strong> oder Zoom) oder für Nachrichten, YouTube und Livestreams Ihren Browser (hier <strong>Google Chrome</strong>). <strong>System Audio (All Applications)</strong> nimmt alles auf, was Ihr Computer wiedergibt. Der Originalton läuft wie gewohnt weiter.',
      screenshot: '03-audio-source.png',
      tip: 'Eine App erscheint oft erst in der Liste, wenn sie Ton wiedergibt. Starten Sie zuerst das Video oder treten Sie dem Anruf bei, und klicken Sie dann auf das Aktualisieren-Symbol.',
    },
    {
      title: 'Übersetzung starten',
      content:
        'Klicken Sie auf <strong>Starten</strong>. Sokuji zeigt die Übersetzung als Untertitel im Hauptfenster an, während das Original weiterläuft. Es wird nichts laut gesprochen: In diesem Modus bleibt <strong>Übersetzung, die ich höre</strong> im Bereich <strong>Sprache</strong> der Einstellungen ausgeschaltet, solange Sie diesen Schalter nicht einschalten.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Die Untertitel über das Video legen',
      content:
        'Klicken Sie in der Titelleiste auf <strong>Untertitel</strong>, um Sokuji in ein schmales Untertitelfenster zu verwandeln. Verschieben Sie es und ändern Sie seine Größe, zum Beispiel über den unteren Rand des Videos, und klicken Sie dann auf <strong>Übersetzung starten</strong>. <strong>Zurück zum Hauptfenster</strong> holt das vollständige Fenster zurück.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Wählen Sie die konkrete App statt System Audio, damit Benachrichtigungstöne und Musik aus anderen Programmen nicht übersetzt werden.',
      'Beenden Sie die Sitzung, wenn das Meeting oder Video vorbei ist: Eine laufende Sitzung verbraucht weiter Ihr Guthaben.',
      'Speichern Sie das Transkript am Ende mit der Download-Schaltfläche in der Symbolleiste.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Brauche ich dafür ein Mikrofon oder VB-CABLE?',
        answer: 'Nein. Im Modus „Gegenüber“ hört Sokuji nur die App ab, die Sie auswählen. Das Mikrofon wird nur gebraucht, wenn Ihre eigene Sprache übersetzt wird, und das virtuelle Audiokabel nur, wenn andere Ihre übersetzte Stimme hören sollen.',
      },
      {
        question: 'Bemerken die anderen Teilnehmenden etwas?',
        answer: 'Nein. Sokuji hört den Ton auf Ihrem eigenen Computer ab und sendet nichts ins Meeting.',
      },
      {
        question: 'Kann Sokuji die Übersetzung auch vorlesen?',
        answer: 'Ja. Schalten Sie vor dem Start im Bereich „Sprache“ der Einstellungen „Übersetzung, die ich höre“ ein und wählen Sie das Gerät, auf dem sie wiedergegeben wird; bei den Sprachen steht dann „Ich höre“ statt „Ich lese“. In der Desktop-App muss dafür „Audio des Gegenübers“ auf eine einzelne App eingestellt sein, nicht auf System Audio, damit Sokuji seine eigene Stimme nicht erneut übersetzt. Das Vorlesen kostet extra.',
      },
      {
        question: 'Kann ich auch in ihrer Sprache antworten?',
        answer: 'Ja. Wechseln Sie in den Modus „Beides“ und folgen Sie der Anleitung für zweiseitige Meetings.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Стежте за зустрічами, новинами та прямими трансляціями іншою мовою',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Цей посібник налаштовує настільний застосунок Sokuji так, щоб те, що ви чуєте, перекладалося в субтитри вашою мовою: зустріч, яку ви лише слухаєте, випуск новин іноземною мовою, відео на YouTube або пряма трансляція. Sokuji лише слухає. На зустріч нічого не надсилається, і вам не потрібні ні мікрофон, ні віртуальний аудіокабель. Використовується KizunaAI (на базі Soniox; рушій Qwen працює так само). Знімки екрана зроблено в Sokuji 0.44.0 на Windows під час перегляду англомовної трансляції в Google Chrome із субтитрами китайською.',
  },
  steps: [
    {
      title: 'Налаштуйте Sokuji з KizunaAI',
      content:
        'Якщо ви ще цього не зробили, дотримуйтеся посібника <a href="/docs/kizuna-ai/quick-start">«Швидкий старт із KizunaAI»</a>, щоб установити Sokuji та увійти в обліковий запис.',
    },
    {
      title: 'Виберіть «Розуміти, що кажуть інші»',
      content:
        'У майстрі налаштування виберіть <strong>Розуміти, що кажуть інші</strong>. Sokuji переходить у режим <strong>Співрозмовник</strong> лише із субтитрами. Якщо ви вже завершили налаштування, натомість натисніть <strong>Співрозмовник</strong> у перемикачі режимів у лівому нижньому куті головного вікна.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте дві мови',
      content:
        'Укажіть у полі <strong>Я читаю</strong> мову, якою потрібні субтитри, а в полі <strong>вони говорять</strong> — мову зустрічі чи відео. Тут: Я читаю китайською, вони говорять англійською.',
      screenshot: '02-languages.png',
      tip: 'Виберіть у полі «Я читаю» конкретну мову, а не «Автовиявлення». Sokuji має знати, якою мовою перекладати, і кнопка «Почати» залишається недоступною, доки ви її не виберете.',
    },
    {
      title: 'Виберіть, що слухає Sokuji',
      content:
        'У налаштуваннях, у розділі <strong>Аудіо співрозмовника</strong>, виберіть застосунок, який відтворює звук: застосунок для зустрічей (наприклад, <strong>Microsoft Teams</strong> або Zoom) чи браузер (тут <strong>Google Chrome</strong>) для новин, YouTube і прямих трансляцій. <strong>System Audio (All Applications)</strong> захоплює все, що відтворює комп’ютер. Оригінальний звук і далі відтворюється як зазвичай.',
      screenshot: '03-audio-source.png',
      tip: 'Застосунок часто з’являється в списку лише тоді, коли відтворює звук. Спочатку запустіть відео або приєднайтеся до дзвінка, потім натисніть значок оновлення.',
    },
    {
      title: 'Почніть переклад',
      content:
        'Натисніть <strong>Почати</strong>. Sokuji показує переклад у вигляді субтитрів у головному вікні, а оригінал продовжує відтворюватися. Уголос нічого не озвучується: у цьому режимі <strong>Переклад, який чую я</strong> в блоці <strong>Мовлення</strong> налаштувань залишається вимкненим, доки ви його не ввімкнете.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Розмістіть субтитри поверх відео',
      content:
        'Натисніть <strong>Субтитри</strong> в рядку заголовка, щоб перетворити Sokuji на вузьке вікно субтитрів. Перемістіть його та змініть розмір, наприклад поверх нижньої частини відео, потім натисніть <strong>Почати переклад</strong>. Кнопка <strong>Повернутися до головного вікна</strong> повертає повне вікно.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Вибирайте конкретний застосунок, а не System Audio, щоб звуки сповіщень і музика з інших програм не перекладалися.',
      'Зупиняйте сеанс, коли зустріч або відео закінчується: запущений сеанс і далі витрачає ваш баланс.',
      'Коли закінчите, збережіть розшифровку кнопкою завантаження на панелі інструментів.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи потрібні для цього мікрофон або VB-CABLE?',
        answer: 'Ні. У режимі «Співрозмовник» Sokuji лише слухає вибраний вами застосунок. Мікрофон потрібен лише тоді, коли перекладається ваше власне мовлення, а віртуальний аудіокабель — лише коли інші мають чути ваш перекладений голос.',
      },
      {
        question: 'Чи помітять щось інші учасники?',
        answer: 'Ні. Sokuji слухає звук на вашому власному комп’ютері й нічого не надсилає на зустріч.',
      },
      {
        question: 'Чи може Sokuji також зачитувати переклад уголос?',
        answer: 'Так. Перед початком увімкніть «Переклад, який чую я» в блоці «Мовлення» налаштувань і виберіть пристрій, на якому він відтворюватиметься; тоді поле «Я читаю» називатиметься «Я чую». У настільному застосунку для цього в «Аудіо співрозмовника» має бути вибрано один застосунок, а не System Audio, щоб Sokuji не перекладав повторно власний голос. Зачитування вголос коштує додатково.',
      },
      {
        question: 'Чи можу я також відповідати їхньою мовою?',
        answer: 'Так. Перемкніться в режим «Обидва» й дотримуйтеся посібника з двосторонніх зустрічей.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Segui riunioni, notiziari e dirette in un’altra lingua',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Questa guida configura l’app desktop di Sokuji per tradurre ciò che senti in sottotitoli nella tua lingua: una riunione in cui ascolti soltanto, un notiziario in lingua straniera, un video di YouTube o una diretta. Sokuji ascolta soltanto. Nella riunione non viene inviato nulla, e non ti servono né un microfono né un cavo audio virtuale. Usa KizunaAI (Con tecnologia Soniox; il motore Qwen funziona allo stesso modo). Le schermate provengono da Sokuji 0.44.0 su Windows, mentre segue una diretta in inglese in Google Chrome con sottotitoli in cinese.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Se non l’hai ancora fatto, segui la <a href="/docs/kizuna-ai/quick-start">guida rapida a KizunaAI</a> per installare Sokuji e accedere.',
    },
    {
      title: 'Scegli “Capire cosa dicono gli altri”',
      content:
        'Nella configurazione guidata, scegli <strong>Capire cosa dicono gli altri</strong>. Sokuji passa in modalità <strong>Altro</strong>, solo con i sottotitoli. Se hai già completato la configurazione, fai invece clic su <strong>Altro</strong> nel selettore di modalità in basso a sinistra nella finestra principale.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Imposta le due lingue',
      content:
        'Imposta <strong>Io leggo</strong> sulla lingua in cui vuoi i sottotitoli e <strong>loro parlano</strong> sulla lingua della riunione o del video. Qui: Io leggo cinese, loro parlano inglese.',
      screenshot: '02-languages.png',
      tip: 'Imposta Io leggo su una lingua specifica, non su Rilevamento Automatico. Sokuji deve sapere in quale lingua tradurre, e Avvia resta disattivato finché non ne scegli una.',
    },
    {
      title: 'Scegli cosa ascolta Sokuji',
      content:
        'In Impostazioni, sotto <strong>Audio dell\'altro</strong>, scegli l’app che riproduce il suono: la tua app per riunioni (per esempio <strong>Microsoft Teams</strong> o Zoom) oppure il browser (qui <strong>Google Chrome</strong>) per notiziari, YouTube e dirette. <strong>System Audio (All Applications)</strong> cattura tutto ciò che il computer riproduce. Il suono originale continua a essere riprodotto come sempre.',
      screenshot: '03-audio-source.png',
      tip: 'Spesso un’app compare nell’elenco solo quando riproduce audio. Avvia prima il video o entra nella chiamata, poi fai clic sull’icona di aggiornamento.',
    },
    {
      title: 'Inizia a tradurre',
      content:
        'Fai clic su <strong>Avvia</strong>. Sokuji mostra la traduzione come sottotitoli nella finestra principale mentre l’originale continua. Nulla viene pronunciato ad alta voce: in questa modalità <strong>Traduzione che sento io</strong>, nella sezione <strong>Voce</strong> delle Impostazioni, resta disattivata a meno che non la attivi tu.',
      screenshot: '04-ready-to-start.png',
    },
    {
      title: 'Metti i sottotitoli sopra il video',
      content:
        'Fai clic su <strong>Sottotitoli</strong> nella barra del titolo per trasformare Sokuji in una sottile finestra di sottotitoli. Spostala e ridimensionala, per esempio sopra la parte inferiore del video, poi fai clic su <strong>Avvia la traduzione</strong>. <strong>Torna alla finestra principale</strong> ripristina la finestra completa.',
      screenshot: '05-subtitle-window.png',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Scegli l’app specifica invece di System Audio, così i suoni delle notifiche e la musica di altri programmi non vengono tradotti.',
      'Interrompi la sessione quando la riunione o il video finisce: una sessione attiva continua a consumare il tuo saldo.',
      'Alla fine, usa il pulsante di download nella barra degli strumenti per salvare la trascrizione.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Mi servono un microfono o VB-CABLE?',
        answer: 'No. In modalità Altro, Sokuji ascolta solo l’app che scegli. Il microfono serve solo quando viene tradotto il tuo parlato, e il cavo audio virtuale solo quando gli altri devono sentire la tua voce tradotta.',
      },
      {
        question: 'Gli altri partecipanti se ne accorgeranno?',
        answer: 'No. Sokuji ascolta l’audio sul tuo computer e non invia nulla nella riunione.',
      },
      {
        question: 'Sokuji può anche leggere ad alta voce la traduzione?',
        answer: 'Sì. Prima di avviare, attiva Traduzione che sento io nella sezione Voce delle Impostazioni e scegli il dispositivo su cui viene riprodotta; l’etichetta della lingua dice allora Io sento invece di Io leggo. Nell’app desktop serve che Audio dell\'altro sia impostato su una sola app, non su System Audio, così Sokuji non traduce di nuovo la propria voce. La lettura ad alta voce costa di più.',
      },
      {
        question: 'Posso anche rispondere nella loro lingua?',
        answer: 'Sì. Passa alla modalità Entrambi e segui la guida alle riunioni bidirezionali.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function UnderstandOthersTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/understand-others" />;
}
